/**
 * One transaction, in full — the phone's tap-a-row sheet (mobile M3).
 *
 * The table on a desktop shows every column at once; a phone row has room for
 * a name, a card and an amount. This sheet holds the rest: the raw
 * description the bank printed, both dates, where the row came from, its
 * flags, and the category editor.
 *
 * A provisional row (from an alert) says so in words and wears the blue
 * "live" treatment — never gold, which means statement-backed money.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, X } from "lucide-react";

import { SheetContent } from "@/components/ui/Sheet";
import { Badge, Button, CardSwatch, SELECT_CLASS } from "@/components/ui/primitives";
import { capsApi, transactionsApi, type Card, type Transaction } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";
import { haptic } from "@/platform";
import { toast } from "sonner";

export function TxnSheet({
  txn,
  card,
  onOpenChange,
}: {
  txn: Transaction | null;
  card: Card | undefined;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: capsApi.categories,
    enabled: txn !== null,
    staleTime: 5 * 60_000,
  });

  const setCategory = useMutation({
    mutationFn: ({ id, categoryId }: { id: string; categoryId: string | null }) =>
      transactionsApi.setCategory(id, categoryId),
    onSuccess: () => {
      haptic("tick");
      void qc.invalidateQueries({ queryKey: ["transactions"] });
      toast.success("Category saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const t = txn;
  const credit = t?.direction === "credit";
  const name = card ? card.nickname || card.product_name || card.issuer : "—";

  return (
    <Dialog.Root open={t !== null} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <SheetContent className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(32rem,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto">
          {t && (
            <div className="space-y-5 px-5 pb-6 pt-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <Dialog.Title className="font-display text-lg font-semibold leading-snug text-ink">
                    {t.merchant || t.description_raw}
                  </Dialog.Title>
                  <Dialog.Description className="mt-1 flex items-center gap-2 text-xs text-faint">
                    <CardSwatch theme={card?.theme ?? null} />
                    {name}
                  </Dialog.Description>
                </div>
                <Dialog.Close asChild>
                  <Button variant="ghost" size="icon" aria-label="Close" className="hidden md:inline-flex">
                    <X className="h-4 w-4" />
                  </Button>
                </Dialog.Close>
              </div>

              <p
                className={cn(
                  "font-display text-[34px] font-semibold tabular-nums tracking-[-0.03em]",
                  t.is_provisional ? "text-live" : credit ? "text-paid" : "text-ink",
                )}
              >
                {credit ? "+" : ""}
                {formatMoney(t.amount)}
              </p>

              {t.is_provisional && (
                <p className="flex items-start gap-2 rounded-xl border border-dashed border-live/40 bg-live/5 px-3 py-2.5 text-xs text-muted">
                  <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-live" aria-hidden />
                  Seen in a transaction alert. The statement row replaces it when the bill arrives.
                </p>
              )}

              <dl className="divide-y divide-hairline rounded-xl border border-hairline">
                <Row label="Date">{formatDate(t.txn_date)}</Row>
                {t.post_date && t.post_date !== t.txn_date && <Row label="Posted">{formatDate(t.post_date)}</Row>}
                <Row label="Direction">{credit ? "Credit" : "Spend"}</Row>
                <Row label="Source">{t.is_provisional ? "Alert email" : "Statement"}</Row>
                <Row label="As printed">
                  <span className="break-words font-mono text-[12px] text-muted">{t.description_raw}</span>
                </Row>
                {t.flags && Object.keys(t.flags).length > 0 && (
                  <Row label="Flags">
                    <span className="flex flex-wrap justify-end gap-1">
                      {Object.keys(t.flags).map((f) => (
                        <Badge key={f} tone={f === "cashback" ? "brass" : "neutral"}>
                          {f.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </span>
                  </Row>
                )}
              </dl>

              <label className="block space-y-1.5">
                <span className="label-micro">Category</span>
                <select
                  className={cn(SELECT_CLASS, "h-12 w-full")}
                  value={t.category_id ?? ""}
                  disabled={setCategory.isPending || !categories}
                  onChange={(e) => {
                    const categoryId = e.target.value || null;
                    setCategory.mutate({ id: t.id, categoryId });
                    onOpenChange(false);
                  }}
                >
                  <option value="">Uncategorised</option>
                  {categories?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </SheetContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3.5 py-2.5 text-sm">
      <dt className="shrink-0 text-faint">{label}</dt>
      <dd className="min-w-0 text-right text-ink">{children}</dd>
    </div>
  );
}
